// MUI-free copy of Ui-components/CommonSections/ComplianceTabs (pill tabs over badge tiles). Not to be confused with
// plain-sections/ComplianceTabs, the healthcare page's logo-card design.
import React, {memo, useEffect, useRef} from 'react';
import parse from 'html-react-parser';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import Container from '@/components/atoms/Container';
import {useRovingTabs} from '@/hooks/useRovingTabs';
import styles from './ComplianceBadgeTabs.module.scss';

export interface ComplianceTabBadge {
    name: string;
    /** Badge artwork. Omitted where no official mark exists; a neutral shield stands in. */
    icon?: string;
    /** `cards` layout only: a one-line description under the name. */
    description?: string;
}

export interface ComplianceTabGroup {
    title: string;
    /** `cards` layout only: a small label above the panel heading. */
    eyebrow?: string;
    /** `cards` layout only: the panel heading. Defaults to the title. */
    heading?: string;
    /** Grouped and `cards` layouts: a short intro under the group title or panel heading. */
    description?: string;
    /** `cards` layout only: show this group's badges as logo tiles (one per standard) instead of icon cards. */
    tiles?: boolean;
    badges: ComplianceTabBadge[];
}

export interface ComplianceBadgeTabsProps {
    heading: string;
    subheading: string;
    groups: ComplianceTabGroup[];
    /** On a white section: dark heading, navy-outlined tabs and bordered badge tiles. Default is the navy band. */
    light?: boolean;
    /** Drop the tabs and show every group at once, one box per group with its badges listed inside. */
    grouped?: boolean;
    /** Keep the tabs, but show the selected group as a heading block over a grid of icon cards (name + description). */
    cards?: boolean;
    /** `light` only: drop the white ground so the page wrapper's background (e.g. a gradient) shows through. */
    transparent?: boolean;
}

// MUI ShieldOutlined path (inlined: not in plain/Icon).
const SHIELD_OUTLINED_PATH =
    'M12 2 4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5zm6 9.09c0 4-2.55 7.7-6 8.83-3.45-1.13-6-4.82-6-8.83v-4.7l6-2.25 6 2.25z';

const LOGO_STYLE = {objectFit: 'contain'} as const;

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

const BadgeLogo = ({badge, size}: {badge: ComplianceTabBadge; size: string}) =>
    badge.icon ? (
        <span className={styles.badgeLogo}>
            <OptimizedImage src={badge.icon} alt={`${badge.name} logo`} fill sizes={size} style={LOGO_STYLE} />
        </span>
    ) : (
        <span className={cx(styles.badgeLogo, styles.badgeFallback)} aria-hidden="true">
            <svg className={styles.fallbackIcon} viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <path d={SHIELD_OUTLINED_PATH} />
            </svg>
        </span>
    );

const ComplianceBadge = memo(function ComplianceBadge({badge, light}: {badge: ComplianceTabBadge; light: boolean}) {
    return (
        <li className={cx(styles.badge, light && styles.badgeLight)}>
            <BadgeLogo badge={badge} size="56px" />
            <span className={`font-headers ${styles.badgeName}`}>{badge.name}</span>
        </li>
    );
});

// `cards` layout: one group's heading block over its icon cards.
const CardsPanel = ({group}: {group: ComplianceTabGroup}) => (
    <div className={styles.cardsPanel}>
        <div className={styles.cardsHeader}>
            {group.eyebrow && <p className={`font-headers ${styles.cardsEyebrow}`}>{group.eyebrow}</p>}
            <h3 className={`font-headers ${styles.cardsHeading}`}>{group.heading ?? group.title}</h3>
            {group.description && <p className={`font-regular-text ${styles.cardsDesc}`}>{group.description}</p>}
        </div>
        {group.tiles ? (
            <ul className={styles.badgeRow}>
                {group.badges.map((badge) => (
                    <ComplianceBadge key={badge.name} badge={badge} light />
                ))}
            </ul>
        ) : (
            <ul className={styles.cardGrid}>
                {group.badges.map((badge) => (
                    <li key={badge.name} className={styles.featureCard}>
                        <span className={styles.featureIconTile}>
                            <BadgeLogo badge={badge} size="24px" />
                        </span>
                        <h4 className={cx('font-headers', styles.featureTitle, !badge.description && styles.featureTitleOnly)}>
                            {badge.name}
                        </h4>
                        {badge.description && <p className={`font-regular-text ${styles.featureDesc}`}>{badge.description}</p>}
                    </li>
                ))}
            </ul>
        )}
    </div>
);

const ComplianceBadgeTabs = ({
    heading,
    subheading,
    groups,
    light = false,
    grouped = false,
    cards = false,
    transparent = false
}: ComplianceBadgeTabsProps) => {
    const {active, getTabProps, getPanelProps} = useRovingTabs(groups.length);
    const tabListRef = useRef<HTMLDivElement>(null);

    // When the chip row scrolls (phones), bring the selected chip into view. Scrolls the row only, never the page.
    useEffect(() => {
        const list = tabListRef.current;
        const tab = list?.children[active] as HTMLElement | undefined;
        if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
        const left = tab.offsetLeft - list.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
        list.scrollTo({left, behavior: 'smooth'});
    }, [active]);

    return (
        <div className={cx(light ? styles.sectionLight : styles.section, light && transparent && styles.sectionTransparent)}>
            <Container>
                <div className={styles.header}>
                    <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                    <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>
                </div>

                {grouped ? (
                    <ul className={styles.groupGrid}>
                        {groups.map((group) => (
                            <li key={group.title} className={styles.groupBox}>
                                <h3 className={`font-headers ${styles.groupTitle}`}>{group.title}</h3>
                                {group.description && <p className={`font-regular-text ${styles.groupDesc}`}>{group.description}</p>}
                                <ul className={styles.groupBadges}>
                                    {group.badges.map((badge) => (
                                        <li key={badge.name} className={styles.groupBadge}>
                                            <BadgeLogo badge={badge} size="44px" />
                                            <span className={`font-regular-text ${styles.groupBadgeName}`}>{badge.name}</span>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className={light ? styles.panelLight : styles.panel}>
                        <div ref={tabListRef} role="tablist" aria-label={heading} className={styles.tabList}>
                            {groups.map((group, index) => (
                                <button
                                    key={group.title}
                                    {...getTabProps(index)}
                                    className={cx('font-regular-text', styles.tab, index === active && styles.tabActive)}
                                >
                                    {group.title}
                                </button>
                            ))}
                        </div>

                        {/* Every panel is server-rendered so all badges are in the HTML; inactive ones are only hidden. */}
                        {groups.map((group, index) => (
                            <div key={group.title} {...getPanelProps(index)}>
                                {cards ? (
                                    <CardsPanel group={group} />
                                ) : (
                                    <ul className={styles.badgeRow}>
                                        {group.badges.map((badge) => (
                                            <ComplianceBadge key={badge.name} badge={badge} light={light} />
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default memo(ComplianceBadgeTabs);
